// Input — design system primitive.
// 28h compact for inline use, 32h for forms. Focus → 2px accent ring.
// See Harness DS v1.0 §08.

import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

type InputSize = "sm" | "md";

const SIZE_CLASSES: Record<InputSize, string> = {
  sm: "h-7 text-[12px] px-2",
  md: "h-8 text-[12px] px-2.5",
};

const BASE =
  "w-full bg-surface text-ink rounded-md border border-border " +
  "outline-none transition-colors duration-120 ease-harness " +
  "placeholder:text-muted " +
  "focus:border-[var(--accent)] focus:shadow-ring " +
  "disabled:bg-surface-2 disabled:text-muted disabled:cursor-not-allowed";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  inputSize?: InputSize;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { inputSize = "sm", className = "", ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={`${BASE} ${SIZE_CLASSES[inputSize]} ${className}`}
      {...rest}
    />
  );
});

/**
 * SearchInput — input with leading icon. Pass icon as a child slot.
 */
export interface SearchInputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: ReactNode;
  inputSize?: InputSize;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    { icon, inputSize = "sm", className = "", ...rest },
    ref,
  ) {
    return (
      <div
        className={`flex items-center gap-2 bg-surface text-ink rounded-md border border-border px-2.5 transition-colors duration-120 ease-harness focus-within:border-[var(--accent)] focus-within:shadow-ring ${SIZE_CLASSES[
          inputSize
        ]
          .split(" ")
          .filter((c) => c.startsWith("h-"))
          .join(" ")} ${className}`}
      >
        {icon ? <span className="text-muted shrink-0">{icon}</span> : null}
        <input
          ref={ref}
          className="grow bg-transparent outline-none text-[12px] placeholder:text-muted text-ink"
          {...rest}
        />
      </div>
    );
  },
);

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Use mono font for code/schema input. */
  mono?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ mono, className = "", ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={`w-full bg-surface text-ink rounded-md border border-border outline-none transition-colors duration-120 ease-harness placeholder:text-muted px-2 py-1.5 text-[12px] leading-[1.5] focus:border-[var(--accent)] focus:shadow-ring ${mono ? "font-mono text-[11px]" : ""} ${className}`}
        {...rest}
      />
    );
  },
);

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  inputSize?: InputSize;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { inputSize = "sm", className = "", children, ...rest },
    ref,
  ) {
    return (
      <select
        ref={ref}
        className={`${BASE} ${SIZE_CLASSES[inputSize]} appearance-none bg-[length:14px_14px] bg-no-repeat bg-[right_0.5rem_center] pr-7 ${className}`}
        style={{
          // Caret as inline SVG so it inherits muted token cleanly.
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>\")",
        }}
        {...rest}
      >
        {children}
      </select>
    );
  },
);
